<template>
  <div class="w-[428px] rounded-[16px] p-[24px] px-[48px] m-10 flex flex-col bg-[#FFFFFF] h-[calc(100vh-80px)]">
    <div class="w-[318px] flex flex-col gap-[40px] flex-grow">
      
      <!-- Logo -->
      <div class="flex items-center">
        <img src="@/assets/onboarding-logo.svg" alt="logo" />
      </div>

      <h3 class="font-minion font-semibold text-[24px] leading-[28.8px] tracking-[0px] text-[#414141]">
        Getting Started and Onboarding
      </h3>

      <!-- Steps -->
      <div class="flex flex-col gap-[32px] h-[calc(100vh-360px)] overflow-y-auto pr-2">
        <div 
          v-for="(step, index) in steps" 
          :key="index" 
          class="flex items-center gap-[16px]" 
          :class="[
            isStepAccessible(step.route) ? 'cursor-pointer' : 'cursor-not-allowed',
          ]"
          @click="navigateTo(step.route)"
        >
          <img :src="getStepIcon(step.route)" alt="Step Icon" class="w-6 h-6" />
          <span 
            :class="[
              currentRoute === step.route ? 'font-bold text-[#00423D]' : 'font-semibold text-[#414141]',
              !isStepAccessible(step.route)
            ]"
            class="text-[16px] leading-[24px]"
            style="font-family: 'Open Sans'; font-weight: 600;"
          >
            {{ step.label }}
          </span>
        </div>
      </div>
    </div>

    <!-- Back Button (Bottom) -->
    <div v-if="previousStep" 
         class="flex items-center cursor-pointer gap-[12px] mt-auto" 
         @click="navigateTo(previousStep.route)">
      <img src="@/assets/back-icon.svg" alt="Back Icon" />
      <div class="text-[#00423D] font-semibold text-[16px] leading-[24px]" 
           style="font-family: 'Open Sans'; font-weight: 600;">
        Back
      </div>
    </div>

  </div>
</template>

<script setup>
import { useRouter, useRoute } from 'vue-router';
import { computed, onMounted } from 'vue';
import currentStepIcon from '@/assets/current_step.svg';
import remainingStepIcon from '@/assets/remaining_steps.svg';
import completedStepIcon from '@/assets/completed_step.svg';
import { useOnboardingStore } from '@/stores/onboarding';

const router = useRouter();
const route = useRoute();
const onboardingStore = useOnboardingStore();

// Status codes
const STATUS_LOCKED = -1;
const STATUS_VISITED = 0;
const STATUS_COMPLETED = 1;

// Steps Array
const steps = [
  { label: 'Welcome', route: '/onboarding/welcome' },
  { label: 'Tell us about Your Organization', route: '/onboarding/organization' },
  { label: 'Tell us about you', route: '/onboarding/about-you' },
  // { label: 'Fundraising & Sustainability Self-Assessment', route: '/onboarding/self-assessment' },
  { label: 'Collaboration & Adding Team Members', route: '/onboarding/team' },
];

const currentRoute = computed(() => route.path);

// Get the current step index
const currentStepIndex = computed(() => {
  return steps.findIndex(step => step.route === currentRoute.value);
});

// Get the next step in sequence
const nextStep = computed(() => {
  const nextIndex = currentStepIndex.value + 1;
  return nextIndex < steps.length ? steps[nextIndex] : null;
});

// Compute Previous Step
const previousStep = computed(() => {
  const currentIndex = currentStepIndex.value;
  return currentIndex > 0 ? steps[currentIndex - 1] : null;
});

// Check if a step is accessible
const isStepAccessible = (stepRoute) => {
  // First step is always accessible
  if (stepRoute === steps[0].route) return true;
  
  // Current step is accessible
  if (stepRoute === currentRoute.value) return true;
  
  // Check store for accessibility
  return onboardingStore.isRouteAccessible(stepRoute);
};

// Navigate Function
const navigateTo = (path) => {
  // Only navigate if the step is accessible
  if (isStepAccessible(path)) {
    router.push(path);
  }
};

// Get the Correct Step Icon
const getStepIcon = (stepRoute) => {
  const status = onboardingStore.getRouteStatus(stepRoute);
  
  if (currentRoute.value === stepRoute) {
    return currentStepIcon;
  }
  if (status === STATUS_COMPLETED) {
    return completedStepIcon;
  }
  return remainingStepIcon;
};

// Initialize
onMounted(async () => {
  await onboardingStore.fetchRouteStatuses();
});
</script>